// Port of TrailData/Import/KMLImporter.swift. Swift streams the file through Foundation's `XMLParser`; the port parses
// it with the browser's `DOMParser` and replays the DOM as the same start / characters / end events (src/gpx/xml.ts),
// driving a line-by-line port of the Swift delegate (`KMLParsingHandler`).

import { isValidCoordinate } from '../core/geo';
import type { DataProvenance, EpochMs, GeoCoordinate, GeoPolygon, RoutePoint } from '../core/models';
import { TrailDataError } from '../data/errors';
import {
  XMLInputError,
  type XMLEventHandler,
  asBytes,
  decodeXMLBytes,
  localName,
  parseXMLText,
  replay,
  rootIsNotBeforeError,
} from '../gpx/xml';
import { ImportAssembler, makeImportFeature, normalizedProperties } from './assembler';
import type { ImportedGeoData } from './importedGeoData';
import { parseDouble } from './propertyMapping';
import { trimWhitespaceAndNewlines } from './text';

/** One parsed `<Placemark>`. */
interface KMLPlacemark {
  id?: string;
  name?: string;
  description?: string;
  extendedData: Record<string, string>;
  points: RoutePoint[];
  lines: RoutePoint[][];
  polygons: GeoPolygon[];
}

/** XML whitespace and Unicode white space, as Swift `Character.isWhitespace` splits on. */
const whitespaceRun = /[\s\u0085]+/u;

function clean(raw: string): string | undefined {
  const trimmed = trimWhitespaceAndNewlines(raw);
  return trimmed === '' ? undefined : trimmed;
}

function kmlPoint(numbers: readonly number[]): RoutePoint | undefined {
  if (numbers.length < 2) return undefined;
  const coordinate: GeoCoordinate = { latitude: numbers[1], longitude: numbers[0] };
  if (!isValidCoordinate(coordinate)) return undefined;
  const altitude = numbers.length > 2 && Number.isFinite(numbers[2]) ? numbers[2] : undefined;
  return altitude === undefined ? { coordinate } : { coordinate, elevation: altitude };
}

/** Event handler collecting placemarks. Used by a single `importData` call only. */
class KMLParsingHandler implements XMLEventHandler {
  readonly placemarks: KMLPlacemark[] = [];
  documentName?: string;
  readonly warnings: string[] = [];
  sawRoot = false;
  notKML = false;

  private readonly stack: string[] = [];
  private text = '';
  private placemark?: KMLPlacemark;
  /** Open `<Polygon>`: exterior and holes collected from its boundaries. */
  private polygonExterior?: GeoCoordinate[];
  private polygonHoles: GeoCoordinate[][] = [];
  /** Open `gx:Track`: positions from `gx:coord`. */
  private trackPositions?: RoutePoint[];
  /** Name of the open `<Data name="…">` / `<SimpleData name="…">`. */
  private dataName?: string;
  private invalidTuples = 0;

  get isStopped(): boolean {
    return this.notKML;
  }

  didStartElement(elementName: string, attribute: (name: string) => string | undefined): void {
    const name = localName(elementName);
    const isRoot = this.stack.length === 0;
    this.stack.push(name);
    this.text = '';

    if (isRoot) {
      if (name !== 'kml') {
        this.notKML = true; // Swift `abortParsing()`
        return;
      }
      this.sawRoot = true;
      return;
    }
    if (!this.placemark && name !== 'Placemark') return;
    switch (name) {
      case 'Placemark': {
        const placemark: KMLPlacemark = { extendedData: {}, points: [], lines: [], polygons: [] };
        const id = attribute('id');
        if (id !== undefined) placemark.id = id;
        this.placemark = placemark;
        break;
      }
      case 'Polygon':
        this.polygonExterior = undefined;
        this.polygonHoles = [];
        break;
      case 'Track':
        this.trackPositions = [];
        break;
      case 'Data':
      case 'SimpleData':
        this.dataName = attribute('name');
        break;
      default:
        break;
    }
  }

  didEndElement(elementName: string): void {
    const name = localName(elementName);
    const stack = this.stack;
    const parent = stack.length >= 2 ? stack[stack.length - 2] : undefined;
    try {
      this.handleEnd(name, parent);
    } finally {
      if (stack.length > 0) stack.pop();
      this.text = '';
    }
  }

  foundCharacters(characters: string): void {
    this.text += characters;
  }

  private handleEnd(name: string, parent: string | undefined): void {
    const placemark = this.placemark;
    const text = this.text;
    if (!placemark) {
      if (name === 'name' && parent === 'Document' && this.documentName === undefined) {
        const cleaned = clean(text);
        if (cleaned !== undefined) this.documentName = cleaned;
      }
      return;
    }
    switch (name) {
      case 'Placemark':
        this.placemarks.push(placemark);
        this.placemark = undefined;
        if (this.invalidTuples > 0) {
          this.warnings.push(`placemark ${this.placemarks.length}: ${this.invalidTuples} invalid coordinate tuple(s) skipped`);
          this.invalidTuples = 0;
        }
        break;
      case 'name':
        if (parent === 'Placemark') this.assignOptional(placemark, 'name', clean(text));
        break;
      case 'description':
        if (parent === 'Placemark') this.assignOptional(placemark, 'description', clean(text));
        break;
      case 'value':
        if (parent === 'Data') {
          const v = clean(text);
          if (this.dataName !== undefined && v !== undefined) placemark.extendedData[this.dataName] = v;
        }
        break;
      case 'SimpleData': {
        const v = clean(text);
        if (this.dataName !== undefined && v !== undefined) placemark.extendedData[this.dataName] = v;
        this.dataName = undefined;
        break;
      }
      case 'Data':
        this.dataName = undefined;
        break;
      case 'coordinates': {
        const positions = this.parseTuples(text);
        const grandparent = this.stack.length >= 3 ? this.stack[this.stack.length - 3] : undefined;
        if (parent === 'Point') {
          if (positions[0]) placemark.points.push(positions[0]);
        } else if (parent === 'LineString') {
          placemark.lines.push(positions);
        } else if (parent === 'LinearRing' && grandparent === 'outerBoundaryIs') {
          this.polygonExterior = positions.map((p) => p.coordinate);
        } else if (parent === 'LinearRing' && grandparent === 'innerBoundaryIs') {
          this.polygonHoles.push(positions.map((p) => p.coordinate));
        }
        break;
      }
      case 'coord':
        if (parent === 'Track') {
          // gx:coord is "lon lat alt" separated by spaces.
          const parts = text
            .split(whitespaceRun)
            .filter((s) => s !== '')
            .map(parseDouble)
            .filter((n): n is number => n !== undefined);
          const point = kmlPoint(parts);
          if (point) this.trackPositions?.push(point);
          else this.invalidTuples += 1;
        }
        break;
      case 'Track':
        if (this.trackPositions) placemark.lines.push(this.trackPositions);
        this.trackPositions = undefined;
        break;
      case 'Polygon':
        if (this.polygonExterior) placemark.polygons.push({ exterior: this.polygonExterior, holes: this.polygonHoles });
        this.polygonExterior = undefined;
        this.polygonHoles = [];
        break;
      default:
        break;
    }
  }

  private assignOptional(placemark: KMLPlacemark, key: 'name' | 'description', value: string | undefined): void {
    if (value === undefined) delete placemark[key];
    else placemark[key] = value;
  }

  /** Parses whitespace-separated `lon,lat[,alt]` tuples; invalid tuples are counted and skipped. */
  private parseTuples(raw: string): RoutePoint[] {
    const out: RoutePoint[] = [];
    for (const tuple of raw.split(whitespaceRun)) {
      if (tuple === '') continue;
      const numbers = tuple.split(',').map(parseDouble);
      const point = numbers.length >= 2 && numbers.every((n) => n !== undefined) ? kmlPoint(numbers as number[]) : undefined;
      if (!point) {
        this.invalidTuples += 1;
        continue;
      }
      out.push(point);
    }
    return out;
  }
}

/**
 * Imports KML 2.2 (Google Earth / My Maps exports and similar).
 *
 * Each `<Placemark>` (at any depth inside `<Document>`/`<Folder>`) becomes records like a GeoJSON feature:
 * - `<Point>` → `PointOfInterest` when `<ExtendedData>` maps to a `POICategory` (same keys and OSM tags as
 *   `GeoJSONImporter`), otherwise an imported `Waypoint` (with a warning);
 * - `<LineString>` (and `gx:Track`) → a stage of the imported trail; several lines in one placemark are joined;
 * - `<Polygon>` (`outerBoundaryIs` + `innerBoundaryIs` holes) → `ProtectedArea`, unless the placemark describes a
 *   facility (then a POI at the centroid);
 * - `<MultiGeometry>` members are collected into the same placemark.
 *
 * Coordinates are `lon,lat[,alt]` tuples separated by whitespace (KML is always WGS84). The placemark `<name>` and
 * `<description>` are used; the document `<name>` names the trail. Element matching is namespace-agnostic and
 * external entities are never resolved. Needs `DOMParser` (browsers, or jsdom in tests).
 */
export class KMLImporter {
  /**
   * Imports KML text (or bytes: encoding from the byte-order mark or the XML declaration, else UTF-8).
   * @throws TrailDataError `decoding` (malformed XML, message "line N: …"), `unsupportedFormat` (root is not `<kml>`),
   *   `noGeometry` (nothing usable).
   */
  importData(data: string | Uint8Array, sourceName: string, importedAt: EpochMs, provenance?: DataProvenance): ImportedGeoData {
    let text: string;
    try {
      text = typeof data === 'string' ? data : decodeXMLBytes(asBytes(data));
    } catch (e) {
      if (e instanceof XMLInputError) throw TrailDataError.decoding(sourceName, `line ${e.line}: ${e.message}`);
      throw e;
    }
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    const handler = new KMLParsingHandler();
    const parsed = parseXMLText(text, 'KMLImporter');
    if (parsed.kind === 'error') {
      if (rootIsNotBeforeError(parsed.source, parsed.error, 'kml')) {
        throw TrailDataError.unsupportedFormat('XML root is not <kml>');
      }
      throw TrailDataError.decoding(sourceName, `line ${parsed.error.line ?? 1}: ${parsed.error.message}`);
    }
    const root = parsed.document.documentElement;
    if (!root) throw TrailDataError.unsupportedFormat('empty document');
    replay(root, handler);
    if (handler.notKML) throw TrailDataError.unsupportedFormat('XML root is not <kml>');
    if (!handler.sawRoot) throw TrailDataError.unsupportedFormat('empty document');

    const assembler = new ImportAssembler(data, sourceName, importedAt, provenance);
    if (handler.documentName !== undefined) assembler.collectionName = handler.documentName;
    for (const warning of handler.warnings) assembler.warn(warning);
    for (const placemark of handler.placemarks) {
      const properties: Record<string, string> = { ...placemark.extendedData };
      if (placemark.name !== undefined && !Object.hasOwn(properties, 'name')) properties.name = placemark.name;
      if (placemark.description !== undefined && !Object.hasOwn(properties, 'description')) {
        properties.description = placemark.description;
      }
      const feature = makeImportFeature(placemark.id, normalizedProperties(properties));
      feature.points = placemark.points;
      feature.lines = placemark.lines;
      feature.polygons = placemark.polygons;
      assembler.add(feature);
    }
    return assembler.finish();
  }
}
